/**
 * StatusToggle Component
 * 
 * Toggle rider status (offline/available)
 * Includes zone selection requirement
 */

import { useState, useEffect } from 'react';
import { useRiderStore } from '@/store/rider';
import { zonesApi } from '@/lib/api';
import { logger } from '@/lib/logger';

export default function StatusToggle() {
  const status = useRiderStore((state) => state.status);
  const selectedZone = useRiderStore((state) => state.selectedZone);
  const availableZones = useRiderStore((state) => state.availableZones);
  const setAvailableZones = useRiderStore((state) => state.setAvailableZones);
  const setSelectedZone = useRiderStore((state) => state.setSelectedZone);
  const goOnline = useRiderStore((state) => state.goOnline);
  const goOffline = useRiderStore((state) => state.goOffline);
  const isOnline = useRiderStore((state) => state.isOnline);

  const [showZoneWarning, setShowZoneWarning] = useState(false);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);

  // Load zones on mount
  useEffect(() => {
    loadZones();
  }, []);

  const loadZones = async () => {
    try {
      setLoadingZones(true);
      const zones = await zonesApi.getAll();
      setAvailableZones(zones);
    } catch (error) {
      logger.error('Failed to load zones', error as Error);
    } finally {
      setLoadingZones(false);
    }
  };

  const isAvailable = status === 'available';
  const isBusy = status === 'busy';

  const [isToggling, setIsToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const handleToggle = async () => {
    if (isToggling) return;

    setIsToggling(true);
    setToggleError(null);

    try {
      if (isAvailable || isBusy) {
        // Go offline
        await goOffline();
        logger.info('User toggled offline');
      } else {
        // Try to go online
        if (!selectedZone) {
          setShowZoneWarning(true);
          setTimeout(() => setShowZoneWarning(false), 3000);
          logger.warn('Cannot go online - no zone selected');
          setIsToggling(false);
          return;
        }
        
        if (!isOnline) {
          logger.warn('Cannot go online - no internet connection');
          setIsToggling(false);
          return;
        }

        await goOnline();
        logger.info('User toggled online');
      }
    } catch (error: any) {
      logger.error('Failed to toggle status', error);
      setToggleError(error.message || 'Failed to update status');
      setTimeout(() => setToggleError(null), 5000);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="space-y-2">
      {/* Status Toggle */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Rider Status</h3>
            <p className="text-sm text-muted-foreground">
              {isAvailable ? 'You are accepting orders' : isBusy ? 'Busy with order' : 'You are offline'}
            </p>
            {selectedZone && (
              <p className="text-xs text-primary mt-1">
                Zone: {selectedZone.name}
              </p>
            )}
          </div>
          
          {/* Toggle Switch */}
          <button
            onClick={handleToggle}
            disabled={isBusy || !isOnline || isToggling}
            className={`relative inline-flex h-12 w-24 items-center rounded-full transition-colors ${
              isAvailable || isBusy
                ? 'bg-primary'
                : 'bg-muted'
            } disabled:opacity-50`}
          >
            <span
              className={`inline-block h-10 w-10 transform rounded-full bg-white shadow-lg transition-transform flex items-center justify-center ${
                isAvailable || isBusy ? 'translate-x-12' : 'translate-x-1'
              }`}
            >
              {isToggling && (
                <span className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              )}
            </span>
          </button>
        </div>

        {/* Network Status */}
        {!isOnline && (
          <div className="mt-3 p-2 bg-orange-100 text-orange-700 rounded text-sm">
            ⚠️ No internet connection
          </div>
        )}

        {/* Zone Warning */}
        {showZoneWarning && (
          <div className="mt-3 p-2 bg-red-100 text-red-700 rounded text-sm">
            ⚠️ Please select a zone first
          </div>
        )}

        {/* Toggle Error */}
        {toggleError && (
          <div className="mt-3 p-2 bg-red-100 text-red-700 rounded text-sm">
            ❌ {toggleError}
          </div>
        )}
      </div>

      {/* Zone Selector (if not selected) */}
      {!selectedZone && status === 'offline' && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="text-sm font-medium text-orange-900 mb-2">
            Select Your Zone
          </div>
          <p className="text-xs text-orange-700 mb-3">
            You need to select a zone before you can go online
          </p>
          <button 
            onClick={() => setShowZoneModal(true)}
            disabled={loadingZones}
            className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {loadingZones ? 'Loading Zones...' : 'Choose Zone'}
          </button>
        </div>
      )}

      {/* Zone Selection Modal */}
      {showZoneModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Select Your Zone</h3>
              <button
                onClick={() => setShowZoneModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              {availableZones.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No zones available</p>
                  <button
                    onClick={loadZones}
                    className="mt-4 px-4 py-2 bg-primary text-white rounded-lg"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableZones.map((zone) => (
                    <button
                      key={zone.id}
                      onClick={() => {
                        setSelectedZone(zone);
                        setShowZoneModal(false);
                        logger.info('Zone manually selected', { zoneId: zone.id, zoneName: zone.name });
                      }}
                      className="w-full p-4 text-left border border-gray-200 rounded-lg hover:bg-primary/10 hover:border-primary transition-colors"
                    >
                      <div className="font-medium">{zone.name}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        Click to select this zone
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}










